import { track } from "@amplitude/analytics-browser";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import {
  addHighlight,
  getHighlightBySlideId,
  updateHighlight,
} from "../../learning-path/learningPath.service";
import {
  AmplitudeEventType,
  type AmplitudeEvent,
  type AmplitudeTrack,
  type GamePlanEventProperties,
} from "../../models/amplitude";
import { Button } from "../../shared/components/button";
import { type GamePlanSlide } from "../wizard/use-game-plan";
import { useStyles } from "./highlight-form.styles";

const TITLE_CHARACTER_LIMIT = 60;
const NOTE_CHARACTER_LIMIT = 256;

const formValidations = z.object({
  title: z.string(),
  note: z.string().min(1, "note is required").max(NOTE_CHARACTER_LIMIT),
});

type HighlightSchema = z.infer<typeof formValidations>;

type Highlight = {
  id: number;
  title: string;
  note: string;
  type: string;
  slideId: number;
};

type Props = {
  slide?: GamePlanSlide;
  title?: string;
  enabledGetHighlight?: boolean;
  amplitudeData?: AmplitudeEvent<GamePlanEventProperties>;
  onClose?: () => void;
  onHighlightSaveSuccess?: () => void;
  onHighlightSaveError?: () => void;
};

export const HighlightForm = (props: Props) => {
  const {
    slide,
    onClose,
    onHighlightSaveSuccess,
    onHighlightSaveError,
    enabledGetHighlight,
    title,
    amplitudeData,
  } = props;
  const classes = useStyles();
  const initial = {
    id: 0,
    title: "",
    note: "",
    type: "Learning",
    slideId: slide?.id,
  };
  const [editableData, setEditableData] = useState<Highlight>(null);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["highlights/slide", slide?.id],
    queryFn: () => getHighlightBySlideId(slide?.id),
    retry: 0,
    refetchOnWindowFocus: false,
    enabled: !!enabledGetHighlight,
  });

  useEffect(() => {
    if (data?.id) {
      setEditableData(data);
      reset(data);
    }
  }, [data]);

  useEffect(() => {
    setEditableData(initial);
  }, []);

  const { mutate: mutateUpdate, isLoading: isUpdating } = useMutation(
    updateHighlight,
    {
      onSuccess: () => {
        onHighlightSaveSuccess();

        queryClient.invalidateQueries({
          queryKey: ["highlights/slide", slide?.id],
        });
      },
      onError: () => {
        onHighlightSaveError();
      },
    }
  );

  const { mutate, isLoading: isAdding } = useMutation(addHighlight, {
    retry: 0,
    onSuccess: () => {
      onHighlightSaveSuccess();

      queryClient.invalidateQueries({
        queryKey: ["highlights/slide", slide?.id],
      });

      if (amplitudeData) {
        const amplitudeEvent: AmplitudeTrack<AmplitudeEventType.HIGHLIGHT_CREATED> =
          {
            event_type: AmplitudeEventType.HIGHLIGHT_CREATED,
            ...amplitudeData,
          };
        track(amplitudeEvent);
      }
    },
    onError: () => {
      onHighlightSaveError();
      queryClient.invalidateQueries({
        queryKey: ["highlights/slide", slide?.id],
      });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<HighlightSchema>({
    resolver: zodResolver(formValidations),
  });
  const watchNote = watch("note", editableData?.note);

  const onSubmit: SubmitHandler<HighlightSchema> = (formData) => {
    const { title, note } = formData;

    const request = {
      ...(editableData?.id && { id: editableData?.id }),
      title,
      note,
      type: "Learning",
      slideId: slide?.id,
    };

    if (editableData?.id) {
      mutateUpdate(request);
    } else {
      mutate(request);
      if (!enabledGetHighlight) reset(initial);
    }
  };

  const disabled = enabledGetHighlight
    ? !isValid || isLoading || isFetching || isAdding || isUpdating
    : !isValid || isAdding;

  return (
    <Paper className={clsx(classes.paper, "highlight-form")}>
      <form data-testid="highlight-form">
        <Grid container style={{ justifyContent: "space-between" }}>
          <DialogTitle
            className={classes.dialogTitle}
            data-testid="alert-dialog-title"
            id="alert-dialog-title"
            title="Add Highlight"
          >
            {title}
          </DialogTitle>
          {enabledGetHighlight && (
            <IconButton
              data-testid="dialog-icon-btn-close"
              name="errors"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Grid>
        <DialogContent>
          {editableData !== null && (
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <TextField
                  {...register("title")}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  id="highlightTitle"
                  label="Title (Optional)"
                  name="title"
                  defaultValue={editableData?.title}
                  autoFocus
                  inputProps={{
                    maxLength: TITLE_CHARACTER_LIMIT,
                    "data-testid": "highlight-title",
                  }}
                  helperText={errors.title?.message}
                  error={!!errors.title}
                />
              </Grid>
              <Grid item xs={12} />
              <Grid item xs={12} md={12}>
                <TextField
                  {...register("note")}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  id="highlightNote"
                  label="Note"
                  name="note"
                  defaultValue={editableData?.note}
                  inputProps={{
                    maxLength: NOTE_CHARACTER_LIMIT,
                    "data-testid": "highlight-note",
                  }}
                  error={!!errors.note}
                  FormHelperTextProps={{ className: classes.helperText }}
                  helperText={
                    errors.note?.message ||
                    `${watchNote?.length || 0}/${NOTE_CHARACTER_LIMIT}`
                  }
                />
              </Grid>
              <Grid item xs={12} />
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="next-button"
            id="wizard-next-button"
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={handleSubmit(onSubmit)}
          >
            Save Highlight
          </Button>
        </DialogActions>
      </form>
    </Paper>
  );
};

HighlightForm.defaultProps = {
  onHighlightSaveSuccess: undefined,
  enabledGetHighlight: true,
  title: "Add Highlight",
};
